/* Video Carousel — Editorial Broadcast (white surface)
   Behavior:
   - Renders exactly the clip blocks; no clones.
   - Middle clip starts active. Auto-advance loops in place via modulo.
   - Clicking a side clip recenters; clicking the active clip toggles play.
   Multi-instance safe; each section runs independently.
*/
(function () {
  'use strict';

  function initCarousel(root) {
    if (!root || root.dataset.poVcInit === '1') return;
    root.dataset.poVcInit = '1';

    var track = root.querySelector('.po-vc__track');
    var prevBtn = root.querySelector('.po-vc__arrow--prev');
    var nextBtn = root.querySelector('.po-vc__arrow--next');
    if (!track) return;

    var clips = Array.prototype.slice.call(track.querySelectorAll('.po-vc__clip'));
    var N = clips.length;
    if (N === 0) return;

    var pointer = Math.floor(N / 2);

    function render(animate) {
      // Always suspend clip width/height transitions while applying the
      // is-active class + measuring. Otherwise the bounding-rect read is
      // mid-transition (clip 2 shrinking from 480->220, clip 3 growing
      // from 220->480) and the centering target is wrong, leaving the
      // active clip off-center after a side-arrow advance. We restore
      // the transitions on the next frame so the visual width change
      // still animates smoothly into the freshly-translated track.
      clips.forEach(function (el) { el.style.transition = 'none'; });
      // Force reflow per clip so the transition: none takes effect
      // before we toggle widths via the .is-active class.
      clips.forEach(function (el) { /* eslint-disable no-unused-expressions */ el.offsetWidth; /* eslint-enable */ });
      clips.forEach(function (el, i) {
        el.classList.toggle('is-active', i === pointer);
      });
      // Force reflow per clip again so new widths are applied
      // before we read getBoundingClientRect for centering.
      clips.forEach(function (el) { el.offsetWidth; });

      // Force a synchronous layout pass so the measurement below sees
      // the post-class-toggle dimensions.
      track.offsetWidth; // eslint-disable-line no-unused-expressions

      // Measure synchronously using offsetLeft/offsetWidth (which are
      // unaffected by in-progress transitions and ignore transforms).
      // This sidesteps the rAF-vs-transition race that left getBounding
      // ClientRect reading stale dimensions during arrow advances.
      var el = clips[pointer];
      var wrap = track.parentElement;
      var wrapStyle = getComputedStyle(wrap);
      var wrapPadLeft = parseFloat(wrapStyle.paddingLeft) || 0;
      // The track has `will-change: transform` which can promote it to a
      // containing block, making clips' offsetLeft track-local rather
      // than wrap-local. Compute the clip's center position relative to
      // the wrap (= track.x_in_wrap + clip.offsetLeft + offsetWidth/2),
      // where track.x_in_wrap is just wrap's padding-left because track
      // is laid out at the start of wrap's content box.
      var elCenterInWrap = wrapPadLeft + el.offsetLeft + el.offsetWidth / 2;
      var translateX = (wrap.clientWidth / 2) - elCenterInWrap;
      track.style.transition = animate ? 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      track.style.transform = 'translateX(' + translateX + 'px)';
      // Restore clip transitions on the next frame so the width change
      // animates smoothly underneath the centered track.
      requestAnimationFrame(function () {
        clips.forEach(function (cl) { cl.style.transition = ''; });
      });
    }

    function pauseClipVideo(clip) {
      if (!clip) return;
      var v = clip.querySelector('video');
      if (v) {
        try { v.pause(); } catch (e) {}
        v.muted = true;
      }
      clip.classList.remove('is-playing', 'is-unmuted');
    }

    function go(idx) {
      idx = ((idx % N) + N) % N;
      if (idx === pointer) return;
      pauseClipVideo(clips[pointer]);
      pointer = idx;
      render(true);
      primeNeighbor(idx);
    }

    // Pre-buffer the now-active clip + its immediate neighbors so the
    // first frames are ready before the user taps play. Browsers will
    // honor this only when preload="metadata" or "auto" is set on the
    // <video> element. Safe to call multiple times — load() is a no-op
    // if the resource is already buffered.
    function primeNeighbor(idx) {
      [idx - 1, idx, idx + 1].forEach(function (i) {
        var c = clips[((i % N) + N) % N];
        if (!c) return;
        var v = c.querySelector('video');
        if (!v) return;
        try { v.preload = 'auto'; v.load(); } catch (_) {}
      });
    }

    function advance(dir) {
      go(pointer + dir);
    }

    track.addEventListener('click', function (e) {
      var soundBtn = e.target.closest && e.target.closest('.po-vc__sound');
      if (soundBtn) {
        e.stopPropagation();
        var soundClip = soundBtn.closest('.po-vc__clip');
        var sv = soundClip && soundClip.querySelector('video');
        if (sv) {
          sv.muted = !sv.muted;
          soundClip.classList.toggle('is-unmuted', !sv.muted);
        }
        return;
      }

      // Don't intercept clicks on the products strip — let the link handle it.
      if (e.target.closest && e.target.closest('.po-vc__product')) return;

      var clip = e.target.closest && e.target.closest('.po-vc__clip');
      if (!clip) return;
      var clickedIdx = clips.indexOf(clip);
      if (clickedIdx === -1) return;

      if (clickedIdx === pointer) {
        var v = clip.querySelector('video');
        if (v) {
          if (v.paused) {
            var p = v.play();
            if (p && typeof p.then === 'function') { p.catch(function () {}); }
            clip.classList.add('is-playing');
            stopAuto();
          } else {
            v.pause();
            v.muted = true;
            clip.classList.remove('is-playing', 'is-unmuted');
          }
        }
      } else {
        go(clickedIdx);
      }
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { advance(-1); stopAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { advance(1); stopAuto(); });

    // Touch / swipe
    var touchStartX = 0;
    var trackWrap = track.parentElement;
    trackWrap.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    trackWrap.addEventListener('touchend', function (e) {
      var diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 40) advance(diff > 0 ? 1 : -1);
    }, { passive: true });

    // Optional auto-advance (loops in place via modulo)
    var autoAdvance = root.dataset.autoAdvance === 'true';
    var autoInterval = parseInt(root.dataset.autoAdvanceInterval, 10);
    if (isNaN(autoInterval) || autoInterval < 2) autoInterval = 6;
    var autoTimer = null;
    function startAuto() {
      if (!autoAdvance || autoTimer) return;
      autoTimer = setInterval(function () { advance(1); }, autoInterval * 1000);
    }
    function stopAuto() {
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }
    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);

    render(false);
    window.addEventListener('resize', function () { render(false); });
    // Pre-buffer the initial active clip so the first play tap is
    // near-instant. Defer one tick so the carousel layout settles
    // before we kick off network requests.
    setTimeout(function () { primeNeighbor(pointer); }, 50);
    startAuto();
  }

  function boot() {
    document.querySelectorAll('.po-vc').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
